import type {
  QuoteRulesetEvaluationDetailPublic,
  QuoteRulesetVersionDetailPublic,
  UserBasicInfo,
} from "@common/client"
import { assetNames, customerNames } from "../../-lib/playbook"

type Version = QuoteRulesetVersionDetailPublic

export type RuleRecord = {
  created_at: string
  created_by: UserBasicInfo | null
  updated_at: string
  last_modified_by: UserBasicInfo | null
}

export type ActivityEntry = {
  key: string
  at: string
  by: UserBasicInfo | null
  text: string
  automatic: boolean
}

function chronological(versions: Version[]): Version[] {
  return [...versions].sort((a, b) => a.number - b.number)
}

export function versionRecord(version: Version): RuleRecord {
  return {
    created_at: version.created_at,
    created_by: version.last_modified_by,
    updated_at: version.created_at,
    last_modified_by: version.last_modified_by,
  }
}

export function ruleRecords(versions: Version[]): Map<string, RuleRecord> {
  const records = new Map<string, RuleRecord>()
  const contents = new Map<string, string>()
  for (const version of chronological(versions)) {
    for (const rule of version.active_rules) {
      const record = records.get(rule.lineage_id)
      if (!record) {
        records.set(rule.lineage_id, versionRecord(version))
      } else if (contents.get(rule.lineage_id) !== rule.content) {
        record.updated_at = version.created_at
        record.last_modified_by = version.last_modified_by
      }
      contents.set(rule.lineage_id, rule.content)
    }
  }
  return records
}

function ruleCount(count: number): string {
  return `${count} ${count === 1 ? "rule" : "rules"}`
}

function ruleChanges(previous: Version, version: Version): string[] {
  const before = new Map(
    previous.active_rules.map((rule) => [rule.lineage_id, rule.content]),
  )
  const kept = new Set(version.active_rules.map((rule) => rule.lineage_id))
  const addedOrUpdated = version.active_rules.flatMap((rule) => {
    const earlier = before.get(rule.lineage_id)
    if (earlier === undefined) {
      return [`Added rule “${rule.content}”`]
    }
    return earlier === rule.content ? [] : [`Updated rule “${rule.content}”`]
  })
  const removed = previous.active_rules
    .filter((rule) => !kept.has(rule.lineage_id))
    .map((rule) => `Removed rule “${rule.content}”`)
  return [...addedOrUpdated, ...removed]
}

function subjectChange(
  axis: string,
  before: string,
  after: string,
  fallback: string,
): string[] {
  return before === after
    ? []
    : [`Changed ${axis} from ${before || fallback} to ${after || fallback}`]
}

function changes(previous: Version, version: Version): string[] {
  const before = previous.active_applicability
  const after = version.active_applicability
  return [
    ...ruleChanges(previous, version),
    ...subjectChange(
      "customer",
      customerNames(before),
      customerNames(after),
      "Any customer",
    ),
    ...subjectChange(
      "asset",
      assetNames(before),
      assetNames(after),
      "Any asset",
    ),
  ]
}

function creation(version: Version): string[] {
  return version.number === 1
    ? [`Created the playbook with ${ruleCount(version.active_rules.length)}`]
    : []
}

function versionEntries(versions: Version[]): ActivityEntry[] {
  const ordered = chronological(versions)
  return ordered.flatMap((version, index) => {
    const previous = index > 0 ? ordered[index - 1] : undefined
    const texts = previous ? changes(previous, version) : creation(version)
    return texts.map((text, position) => ({
      key: `${version.id}:${position}`,
      at: version.created_at,
      by: version.last_modified_by,
      text,
      automatic: false,
    }))
  })
}

function evaluationEntries(
  evaluations: QuoteRulesetEvaluationDetailPublic[],
): ActivityEntry[] {
  return evaluations.map((evaluation) => {
    const fired = evaluation.active_results.filter(
      ({ verdict }) => verdict === "applied",
    ).length
    const quote = evaluation.quote?.number ?? "a quote"
    return {
      key: evaluation.id,
      at: evaluation.created_at,
      by: null,
      text: `Applied on ${quote} · ${ruleCount(fired)} fired`,
      automatic: true,
    }
  })
}

export function activity(
  versions: Version[],
  evaluations: QuoteRulesetEvaluationDetailPublic[],
): ActivityEntry[] {
  return [...versionEntries(versions), ...evaluationEntries(evaluations)].sort(
    (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime(),
  )
}
